<template>
  <v-card :loading="loading" class="elevation-0 pa-4">
    <v-card-title class="subtitle font-weight-thin fluid"
      >{{ releaseNoteRecord.title }} (id: {{ releaseNoteRecord.id }})</v-card-title
    >
    <v-card-text>
      <v-form v-model="formValid" v-show="!loading">
        <v-row>
          <v-col sm="12" md="8">
            <user-picker
              attributeName="testPlanAssignedTo"
              v-model="testPlanRecord.assignedTo"
              label="Assigned-To User"
              @input="selectAssignedToUser"
              outlined
            ></user-picker>

            <v-autocomplete
              v-model="testPlanRecord.status"
              :items="testingStatusDropdowns"
              label="Testing Status"
              :rules="testingStatusRules"
              @change="setImplementLaterFlag"
              data-cy="testPlanForm__input--testingStatus"
              outlined
            ></v-autocomplete>

            <v-autocomplete
              v-if="testPlanRecord.status === 'Implement Later'"
              v-model="testPlanRecord.implementationStatus"
              :items="implementationStatusDropdowns"
              label="Implementation Status"
              :rules="implementationStatusRules"
              data-cy="testPlanForm__input--implementationStatus"
              outlined
            ></v-autocomplete>

            <v-checkbox
              label="Reported"
              v-model="testPlanRecord.reported"
              :false-value="0"
              :true-value="1"
              @change="setReportedValues"
              :ripple="false"
              data-cy="testPlanForm__checkbox--reported"
            ></v-checkbox>

            <v-menu
              v-if="!!testPlanRecord.reported"
              ref="reportedDateMenu"
              :close-on-content-click="false"
              v-model="reportedDateMenu"
              :nudge-right="40"
              :return-value.sync="testPlanRecord.reportedDate"
              transition="scale-transition"
              offset-y
              min-width="100px"
              outlined
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :rules="reportedDateRules"
                  clearable
                  data-cy="testPlanForm__input--reportedDate"
                  label="Reported Date *"
                  prepend-icon="event"
                  slot="activator"
                  v-model="testPlanRecord.reportedDate"
                  v-on="on"
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker v-model="testPlanRecord.reportedDate" no-title scrollable>
                <v-spacer></v-spacer>
                <v-btn text @click="reportedDateMenu = false">Cancel</v-btn>
                <v-btn text @click="$refs.reportedDateMenu.save(testPlanRecord.reportedDate)">OK</v-btn>
              </v-date-picker>
            </v-menu>

            <v-textarea
              data-cy="testPlanForm__input--reportedComments"
              v-if="!!testPlanRecord.reported"
              v-model="testPlanRecord.reportedComments"
              label="Reported Comments"
              outlined
            ></v-textarea>

            <v-card-actions>
              <v-btn @click="cancel" data-cy="testPlanForm__button--cancel" outlined>Cancel</v-btn>
              <v-btn @click="save" :disabled="!formValid" color="primary" data-cy="testPlanForm__button--submit"
                >Save Release Note</v-btn
              >
            </v-card-actions>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import PackInfo from "@/components/packInfo";
import UserPicker from "@/components/userPicker";

import { mapGetters } from "vuex";

export default {
  name: "testPlanFormComponent",
  components: {
    PackInfo,
    UserPicker,
  },
  computed: {
    dropdowns() {
      return this.$store.state.dropdown.dropdowns;
    },
    implementationStatusDropdowns() {
      return this.$store.state.dropdown.dropdowns.implementationStatus;
    },
    historicallyNotImpactedFeatureFlag() {
      return this.featureFlagMap.historicallyNotImpacted;
    },
    releaseNoteRecord() {
      return this.$store.state.releaseNote.releaseNoteRecord;
    },
    testPlanRecord() {
      return this.$store.state.releaseNote.testPlanRecord;
    },
    testingStatusDropdowns() {
      const unfilteredTestingStatus = this.$store.state.dropdown.dropdowns.testingStatus;

      let statusList = unfilteredTestingStatus;
      if (!this.historicallyNotImpactedFeatureFlag) {
        // TODO: Remove this filtering when the historicallyNotImpacted feature flag is removed
        statusList = unfilteredTestingStatus.filter(item => item.value !== "Historically Not Impacted");
      }
      const operationalizeType = this.$store.state.releaseNote.releaseNoteRecord.operationalizeType;
      const releaseType = this.$store.state.releaseNote.releaseNoteRecord.releaseType;
      const templateType = this.$store.state.releaseNote.releaseNoteRecord.templateType;

      let statusListFiltered = statusList.filter(status => {
        let hasOperationalizeTypeFilter = true;
        if (operationalizeType) {
          hasOperationalizeTypeFilter = !status.filter.operationalizeType.includes(operationalizeType);
        }

        let hasReleaseTypeFilter = true;
        if (releaseType) {
          hasReleaseTypeFilter = !status.filter.releaseType.includes(releaseType);
        }

        let hasTemplateTypeFilter = true;
        if (templateType) {
          hasTemplateTypeFilter = !status.filter.templateType.includes(templateType);
        }

        return hasOperationalizeTypeFilter && hasReleaseTypeFilter && hasTemplateTypeFilter;
      });

      statusListFiltered = statusListFiltered.map(status => status.value);

      return statusListFiltered;
    },
    ...mapGetters({
      featureFlagMap: "featureFlag/getFlagMap",
    }),
  },
  async created() {
    this.loading = true;
    try {
      await Promise.all([
        this.$store.dispatch("releaseNote/getReleaseNoteRecord", this.releaseNoteId),
        this.$store.dispatch("releaseNote/getTestPlanRecord", {
          releaseNoteId: this.releaseNoteId,
          serviceAreaId: this.serviceAreaId,
        }),
      ]);
    } catch (e) {
      console.error(e);
      this.$notifyError("Could not load form data.");
    }
    this.loading = false;

    this.updateImplementationStatusRules(this.testPlanRecord.status);
  },
  data() {
    return {
      formValid: true,
      implementationStatusRules: [],
      loading: false,
      reportedDateMenu: false,
      reportedDateRules: [],
      testingStatusRules: [v => !!v || "Testing Status is required"],
    };
  },
  methods: {
    cancel() {
      this.$router.push({
        name: "testPlanView",
        params: {
          packId: this.packId,
          releaseNoteId: this.releaseNoteId,
          serviceAreaId: this.serviceAreaId,
        },
      });
    },
    setImplementLaterFlag(val) {
      if (val === "Implement Later") {
        this.testPlanRecord.implementLater = 1;
      } else {
        this.testPlanRecord.implementationStatus = null;
        this.testPlanRecord.implementLater = 0;
      }
      this.updateImplementationStatusRules(val);
    },
    async save() {
      try {
        await this.$store.dispatch("releaseNote/updateTestPlan", this.testPlanRecord);
        this.saveTestPlanSuccess();
        this.$router.push({
          name: "testPlanView",
          params: {
            packId: this.packId,
            releaseNoteId: this.releaseNoteId,
            serviceAreaId: this.serviceAreaId,
          },
        });
      } catch (e) {
        console.error(e);
        this.saveTestPlanError();
      }
    },
    selectAssignedToUser(val) {
      if (val) {
        this.testPlanRecord.assignedTo = val;
      } else {
        this.testPlanRecord.assignedTo = null;
      }
    },
    setReportedValues(val) {
      if (val === 0) {
        this.testPlanRecord.reportedDate = null;
        this.testPlanRecord.reportedComments = null;
      }
      this.updateReportedDateRules(val);
    },
    updateImplementationStatusRules(val) {
      if (val === "Implement Later") {
        this.implementationStatusRules = [v => !!v || "Implementation Status is required"];
      } else {
        this.implementationStatusRules = [];
      }
    },
    updateReportedDateRules(val) {
      if (val === 1) {
        this.reportedDateRules = [v => !!v || "Reported Date is required"];
      } else {
        this.reportedDateRules = [];
      }
    },
  },
  notifications: {
    saveTestPlanError: {
      title: "Error",
      message: "Could not save Test Plan update",
      type: "error",
    },
    saveTestPlanSuccess: {
      title: "Saved!",
      message: "Successfully saved Test Plan update",
      type: "success",
    },
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
    packId: {},
    releaseNoteId: {
      default: null,
    },
    serviceAreaId: {
      default: null,
    },
  },
};
</script>

<style>
.ck-editor__editable {
  min-height: 100px;
}
</style>
